import { buildWabunkaGuestTypeViewModel, type WabunkaGuestTypeViewModel } from '@/wabunka-src/features/Experience/models/guestType';

import type { WabunkaExperienceEventFee } from '@japan-culture-and-technology/wabunka-protos/ts_proto/wabunka_experience_event_fee';

/**
 * 体験イベント料金ビューモデル
 */
export interface WabunkaExperienceEventFeeViewModel {
  /** 体験イベントID */
  experienceEventId: string;
  /** 体験料金ID */
  experienceFeeId: string;
  /** ゲストタイプ */
  guestType: WabunkaGuestTypeViewModel;
}

/**
 * 体験イベント料金のデフォルト値を返却する
 */
export const createDefaultWabunkaExperienceEventFeeViewModel = (): WabunkaExperienceEventFeeViewModel => ({
  experienceEventId: '',
  experienceFeeId: '',
  guestType: {
    id: '',
    experienceId: '',
    typeName: '',
    name: '',
    overview: '',
    description: '',
    sequenceId: 0,
    isIncludedInHeadcountLimit: false,
    experienceFees: [],
  },
});

/**
 * APIレスポンスから体験イベント料金ビューモデルを作成する
 */
export const buildWabunkaExperienceEventFeeViewModel = (res: WabunkaExperienceEventFee | undefined): WabunkaExperienceEventFeeViewModel => {
  if (res == null) {
    return createDefaultWabunkaExperienceEventFeeViewModel();
  }

  // guestTypeをビューモデルに変換
  const guestTypeViewModel = buildWabunkaGuestTypeViewModel(res.guestType);

  return {
    experienceEventId: res.experienceEventId,
    experienceFeeId: res.experienceFeeId,
    guestType: guestTypeViewModel,
  };
};

/**
 * 体験イベント料金リスト表示用のビューモデルを組み立てる
 * ゲストタイプのsequenceIdで昇順ソートされる
 */
export const buildWabunkaExperienceEventFeeListViewModel = (fees: WabunkaExperienceEventFee[] | undefined): WabunkaExperienceEventFeeViewModel[] => {
  if (!fees) {
    return [];
  }

  return fees.map(buildWabunkaExperienceEventFeeViewModel).sort((a, b) => a.guestType.sequenceId - b.guestType.sequenceId);
};

/**
 * 単語を複数形にする。
 * 不規則複数形・不変化形はここでマッピングする。
 * - "child" → "children"
 * - "youth" → "youth"（チケット/予約業界の慣習で複数形でも不変化）
 * 将来的にゲストタイプ名が増えた場合（例: "person" → "people"）は、ここにエントリを追加する。
 * それ以外は末尾 "s" で複数形化する。
 */
function pluralizeWord(word: string): string {
  const lower = word.toLowerCase();
  if (lower === 'child') return 'children';
  if (lower === 'youth') return lower;
  return `${lower}s`;
}

/**
 * ゲストタイプの表示名を複数形にする。
 * - 単語が1つ（例: "Child", "Adult"）: そのまま小文字、複数は上記ルール
 * - 複数語（例: "Child Participant", "Child Observer"）: 最後の語だけ複数形にして結合
 */
function pluralizeGuestTypeName(name: string, count: number): string {
  const words = name.trim().split(/\s+/);
  const base = name.toLowerCase().trim();
  if (count === 1) {
    return base;
  }
  if (words.length === 1) {
    return pluralizeWord(words[0]);
  }
  const lastWord = words[words.length - 1];
  const rest = words.slice(0, -1).join(' ').toLowerCase();
  return `${rest} ${pluralizeWord(lastWord)}`;
}

/**
 * ゲストタイプ別人数を "3 adults, 2 children, 1 observer" 形式のラベルに変換する。
 * experienceEventFees は既に sequenceId でソート済みであることを前提とする。
 *
 * @param guestCounts - ゲストタイプIDごとの人数
 * @param experienceEventFees - 体験イベント料金（ゲストタイプ情報付き、ソート済み）
 * @returns 表示用ラベル文字列。該当なしの場合は空文字
 */
export function formatGuestCountsLabel(guestCounts: Record<string, number>, experienceEventFees: WabunkaExperienceEventFeeViewModel[] | undefined): string {
  if (!guestCounts || !experienceEventFees || experienceEventFees.length === 0) {
    return '';
  }

  const parts: string[] = [];

  for (const fee of experienceEventFees) {
    const count = guestCounts[fee.guestType.id];
    if (count == null || count <= 0) {
      continue;
    }
    const label = pluralizeGuestTypeName(fee.guestType.name, count);
    parts.push(`${count} ${label}`);
  }

  return parts.join(', ');
}
